package at.theatervogelweide.admin.web.components

import at.theatervogelweide.markdown.MarkdownNode
import at.theatervogelweide.markdown.parseMarkdown

/*
 * The formatting toolbar's changes to the text (#166). What is formatted already comes from the site's own Markdown
 * parser (parseMarkdown), so the toolbar sees links, emphasis, lists, headings and code as the site renders them.
 */

/**
 * One change of the formatting toolbar: replace [start]..[end] of the text with [replacement], then select
 * [selectionStart]..[selectionEnd] of the changed text. One replacement, so the text area undoes it in one step.
 */
data class MarkdownEdit(
    val start: Int,
    val end: Int,
    val replacement: String,
    val selectionStart: Int,
    val selectionEnd: Int,
) {
    fun applyTo(text: String): String = text.substring(0, start) + replacement + text.substring(end)
}

/** The inline formats: the parser's part, and the [marker] the toolbar writes on both sides. */
enum class InlineFormat(internal val type: String, val marker: String) {
    BOLD("STRONG", "**"),
    ITALIC("EMPH", "*"),
}

/** The formats of whole lines, each a marker at the start of the line. */
enum class LineFormat {
    BULLET_LIST,
    NUMBERED_LIST,
    HEADING,
}

/**
 * Bold or italic on the selection, or with none on the word at the cursor; without a word, empty markers with the
 * cursor between them. Text inside that format already, with or without its markers selected, loses it instead,
 * also from under other formats (`_**x**_`). A selection across paragraphs or list items is marked in each, as
 * emphasis can't span them, and stays selected with its markers, so a second press takes them off again.
 */
fun toggleInline(text: String, selectionStart: Int, selectionEnd: Int, format: InlineFormat): MarkdownEdit {
    val marker = format.marker
    var start = selectionStart
    var end = selectionEnd
    if (start == end) {
        while (start > 0 && text[start - 1].isWordChar()) start--
        while (end < text.length && text[end].isWordChar()) end++
    } else {
        // a double click also selects the space after the word
        while (start < end && text[start].isWhitespace()) start++
        while (end > start && text[end - 1].isWhitespace()) end--
    }

    // the empty markers a press without a word wrote
    if (start == end && start >= marker.length && text.startsWith(marker + marker, start - marker.length) &&
        text.getOrNull(start - marker.length - 1) != '*' && text.getOrNull(start + marker.length) != '*'
    ) {
        return MarkdownEdit(start - marker.length, start + marker.length, "", start - marker.length, start - marker.length)
    }

    val tree = parseMarkdown(text)
    val nodes = tree.all().toList()
    // code, images, bare addresses and a link's syntax can't hold markers: they take them around the whole
    while (true) {
        val whole = nodes.firstOrNull { node ->
            node.start < end && start < node.end && (node.start < start || end < node.end) && when (node.type) {
                "CODE_SPAN", "IMAGE", "AUTOLINK", "GFM_AUTOLINK" -> true
                in LINKS -> node.part("LINK_TEXT").let { label -> label == null || start < label.start + 1 || label.end - 1 < end }
                else -> false
            }
        } ?: break
        start = minOf(start, whole.start)
        end = maxOf(end, whole.end)
    }
    val emphasis = nodes.filter { !it.leaf && (it.type == "EMPH" || it.type == "STRONG") }
    // a selection with its markers counts as the text inside them
    while (true) {
        val node = emphasis.firstOrNull { it.start == start && it.end == end } ?: break
        start = node.contentStart
        end = node.contentEnd
    }
    val formatted = emphasis.filter { it.type == format.type && start < end && it.contentStart <= start && end <= it.contentEnd }
        .maxByOrNull { it.start }
    if (formatted != null) {
        val length = formatted.contentStart - formatted.start
        val content = text.substring(formatted.contentStart, formatted.contentEnd)
        return MarkdownEdit(formatted.start, formatted.end, content, start - length, end - length)
    }

    val blocks = nodes.filter { !it.leaf && it.type in INLINE_BLOCKS }
        .mapNotNull { block -> trimmed(text, maxOf(block.start, start), minOf(block.end, end)) }.toList()
    if (blocks.isEmpty()) {
        // text outside paragraphs, headings and cells is code or syntax, such as a reference definition or a heading's
        // `#`: nothing there can be bold or italic. A cursor takes the markers only among text or at the end of a line
        // outside that syntax.
        fun inText(at: Int) = nodes.any { block ->
            !block.leaf && block.type in INLINE_BLOCKS && trimmed(text, block.start, block.end)?.let { (from, to) -> at in from..to } == true
        }
        val lineEnd = text.indexOf('\n', start).takeIf { it >= 0 } ?: text.length
        val atLineEnd = text.substring(start, lineEnd).isBlank() &&
            nodes.none { !it.leaf && (it.type in SYNTAX_BLOCKS || it.type in HEADINGS) && it.start <= start && start <= it.end }
        if (start < end || !(inText(start) || atLineEnd)) {
            return MarkdownEdit(selectionStart, selectionStart, "", selectionStart, selectionEnd)
        }
        return MarkdownEdit(start, end, marker + marker, start + marker.length, end + marker.length)
    }
    // marked already, block by block, whether the selection takes the markers or ends among them
    fun markedBlock(from: Int, to: Int) = emphasis.firstOrNull { it.type == format.type && it.start <= from && to <= it.end }
    if (blocks.size == 1 && markedBlock(blocks.single().first, blocks.single().second) == null) {
        // only the part of the selection that emphasis can mark, not code or a table's lines around it
        val (from, to) = blocks.single()
        return MarkdownEdit(from, to, marker + text.substring(from, to) + marker, from + marker.length, to + marker.length)
    }
    val unmark = blocks.all { (from, to) -> markedBlock(from, to) != null }
    val changes = blocks.flatMap { (from, to) ->
        if (unmark) {
            val node = markedBlock(from, to)!!
            listOf(Change(node.start, node.contentStart, ""), Change(node.contentEnd, node.end, ""))
        } else {
            listOf(Change(from, from, marker), Change(to, to, marker))
        }
    }
    return combine(text, start, end, changes) { map -> map(start, false) to map(end, true) }
}

/**
 * A list or the heading on the selected lines, or the cursor's line; blank lines among them stay as they are. Lines
 * that all have it already lose it; a list button on lines of the other list kind switches them. Markers go after a
 * quote's `>` and, for a heading, after a list item's marker.
 */
fun toggleLines(text: String, selectionStart: Int, selectionEnd: Int, format: LineFormat): MarkdownEdit {
    val start = text.lastIndexOf('\n', selectionStart - 1) + 1
    // a selection that ends at the start of a line doesn't take that line
    val last = if (selectionEnd > selectionStart && text[selectionEnd - 1] == '\n') selectionEnd - 1 else selectionEnd
    val end = text.indexOf('\n', last).takeIf { it >= 0 } ?: text.length
    val lines = lineRanges(text, start, end)
    val targets = lines.filter { text.substring(it.first, it.last).isNotBlank() }.ifEmpty { lines }

    val tree = parseMarkdown(text)
    val nodes = tree.all().toList()
    val quoteMarkers = nodes.filter { it.leaf && it.type == "BLOCK_QUOTE" }
    val quotes = nodes.filter { !it.leaf && it.type == "BLOCK_QUOTE" }
    // the `> ` of quotes a lazy line belongs to without writing them, which a new marker on it has to write
    fun missingQuotes(line: IntRange) =
        "> ".repeat(maxOf(0, quotes.count { it.start < line.first && line.first < it.end } - quoteMarkers.count { it.start in line }))
    val items = tree.items(text)
    fun quoteEnd(line: IntRange) = quoteMarkers.filter { it.start in line }.maxOfOrNull { it.end } ?: line.first
    // a marker for the line from `from` to `to`, with the quotes it needs written before the indent that keeps the line
    // in its item
    fun mark(line: IntRange, from: Int, to: Int, marker: String): List<Change> {
        val quotes = missingQuotes(line)
        val at = quoteEnd(line)
        return if (quotes.isEmpty() || at == from) listOf(Change(from, to, quotes + marker)) else listOf(Change(at, at, quotes), Change(from, to, marker))
    }
    fun itemAt(line: IntRange) = items.firstOrNull { it.marker.start in line }
    // a line of an item's text after its first: its indent keeps it in the item
    fun continues(line: IntRange) = itemAt(line) == null && items.any { it.start < line.first && line.first < it.end }
    // where the line's own text starts, after its quote marker, list marker and indent
    fun textStart(line: IntRange): Int {
        var at = itemAt(line)?.marker?.end ?: quoteEnd(line)
        while (at < line.last && (text[at] == ' ' || text[at] == '\t')) at++
        return at
    }

    // the indent a lazy line of an item's text (`- Eltern\nFortsetzung`) needs to stay in it once it has a marker;
    // selected with the item, it becomes the item's sibling
    fun missingIndent(line: IntRange): String {
        if (!continues(line)) return ""
        val owner = items.filter { it.start < line.first && line.first < it.end }.maxByOrNull { it.start } ?: return ""
        if (targets.any { owner.marker.start in it }) return ""
        val ownerStart = text.lastIndexOf('\n', owner.marker.start - 1) + 1
        val ownerLine = ownerStart..(text.indexOf('\n', ownerStart).takeIf { it >= 0 } ?: text.length)
        fun width(line: IntRange, from: Int, to: Int) = column(text, line.first, to) - column(text, line.first, from)
        return " ".repeat(maxOf(0, width(ownerLine, quoteEnd(ownerLine), owner.marker.end) - width(line, quoteEnd(line), textStart(line))))
    }

    // code and reference definitions take no heading or list marker; but a line indented by mistake, which reads as
    // code, becomes an item
    fun inSyntax(line: IntRange, types: Set<String> = SYNTAX_BLOCKS) = nodes.any { !it.leaf && it.type in types && it.start < line.last && line.first < it.end }

    val changes = mutableListOf<Change>()
    var firstText = textStart(targets.first())
    if (format == LineFormat.HEADING) {
        val headings = nodes.filter { !it.leaf && it.type in HEADINGS }
        fun headingAt(line: IntRange) = headings.firstOrNull { it.start <= line.last && it.end > line.first }
        // an empty heading (`#`) has no text
        fun headingText(heading: MarkdownNode) = headingText(text, heading) ?: (heading.end to heading.end)
        val headingLines = targets.filterNot(::inSyntax)
        if (headingLines.isEmpty()) {
            return MarkdownEdit(selectionStart, selectionStart, "", selectionStart, selectionEnd)
        }
        headingAt(targets.first())?.let { firstText = maxOf(firstText, headingText(it).first) }
        if (headingLines.all { headingAt(it) != null }) {
            // the markers before the text, the closing hashes or a setext underline after it
            headingLines.mapNotNull(::headingAt).distinct().forEach { heading ->
                val (from, to) = headingText(heading)
                changes += Change(heading.start, from, "")
                changes += Change(to, heading.end, "")
            }
        } else {
            headingLines.filter { headingAt(it) == null }.forEach { line ->
                val prefix = itemAt(line)?.marker?.end ?: if (continues(line)) textStart(line) else quoteEnd(line)
                // an empty item's marker (`-`) needs its space before the hash
                val space = if (prefix > line.first && text[prefix - 1] != ' ' && text[prefix - 1] != '\t') " " else ""
                changes += mark(line, prefix, textStart(line), missingIndent(line) + "$space# ")
            }
        }
    } else {
        val bullet = format == LineFormat.BULLET_LIST
        // an item's own marker changes even when code follows it on its line
        val listLines = targets.filter { itemAt(it) != null || !inSyntax(it, SYNTAX_BLOCKS - "CODE_BLOCK") }
        if (listLines.isEmpty()) {
            return MarkdownEdit(selectionStart, selectionStart, "", selectionStart, selectionEnd)
        }
        if (listLines.all { line -> itemAt(line)?.bullet == bullet }) {
            listLines.forEach { line -> itemAt(line)?.let { changes += Change(it.marker.start, it.marker.end, "") } }
            // the escape a marker brought along for text that reads as a marker itself (`1. 2\. Vorstellung`) goes
            // too, where the text stays text without it
            var escapes = listLines.filter { itemAt(it) != null }.mapNotNull { line ->
                val at = textStart(line)
                ESCAPED_LIST_LIKE.find(text.substring(at, line.last))?.let { Change(at + it.value.length, at + it.value.length + 1, "") }
            }
            while (escapes.isNotEmpty()) {
                val all = changes + escapes
                val result = combine(text, start, end, all) { it(start, false) to it(end, false) }.applyTo(text)
                fun moved(position: Int) = position + all.filter { it.end <= position }.sumOf { it.text.length - (it.end - it.start) }
                val markers = parseMarkdown(result).all().filter { it.leaf && (it.type == "LIST_BULLET" || it.type == "LIST_NUMBER") }.toList()
                val kept = escapes.filter { escape ->
                    val at = moved(escape.start)
                    val lineStart = result.lastIndexOf('\n', at - 1) + 1
                    markers.none { it.start in lineStart..at }
                }
                if (kept.size == escapes.size) break
                escapes = kept
            }
            changes += escapes
        } else {
            // a converted item's text column, which its nested items' indents follow, and the numbers per depth
            val textColumns = mutableMapOf<ListItem, Int>()
            // how far a converted item's text moved, which the lines below it in the item move along with
            val shifts = mutableMapOf<ListItem, Int>()
            val numbers = mutableListOf<Int>()
            listLines.forEach { line ->
                val item = itemAt(line)
                val depth = item?.depth ?: 0
                while (numbers.size > depth + 1) numbers.removeLast()
                while (numbers.size < depth + 1) numbers += 0
                numbers[depth]++
                val newMarker = if (bullet) "- " else "${numbers[depth]}. "
                val from = if (continues(line)) textStart(line) else quoteEnd(line)
                if (item != null) {
                    // in columns, as a tab takes up to 4
                    fun width(to: Int) = column(text, line.first, to) - column(text, line.first, from)
                    val indent = " ".repeat(item.parent?.let(textColumns::get) ?: width(item.marker.start))
                    textColumns[item] = indent.length + newMarker.length
                    shifts[item] = indent.length + newMarker.length - width(item.marker.end)
                    changes += Change(from, item.marker.end, indent + newMarker)
                } else {
                    val to = textStart(line)
                    changes += mark(line, from, to, missingIndent(line) + newMarker)
                    // text that would read as a list marker itself stays text: `1. 2\. Vorstellung`, `- \- Beispiel`
                    LIST_LIKE.find(text.substring(to, line.last))?.let {
                        val at = if (it.value.first().isDigit()) to + it.value.length else to
                        changes += Change(at, at, "\\")
                    }
                }
            }
            lineRanges(text, 0, text.length).filter { line -> line !in listLines && text.substring(line.first, line.last).isNotBlank() }.forEach { line ->
                val owner = shifts.keys.filter { it.start < line.first && line.first < it.end }.maxByOrNull { it.start } ?: return@forEach
                val shift = shifts.getValue(owner)
                // after the quotes around the item, before any inside it
                val around = quotes.filter { owner.start in it.start until it.end }
                val at = quoteMarkers.filter { marker -> marker.start in line && around.any { marker.start in it.start until it.end } }
                    .maxOfOrNull { it.end } ?: line.first
                // in columns, as a tab in the indent takes up to 4
                val indent = text.substring(at, line.last).takeWhile { it == ' ' || it == '\t' }.length
                val width = column(text, line.first, at + indent) - column(text, line.first, at)
                if (shift != 0) {
                    changes += Change(at, at + indent, " ".repeat(maxOf(0, width + shift)))
                }
            }
        }
    }

    return combine(text, start, end, changes) { map ->
        val floor = map(firstText, true)
        if (selectionStart == selectionEnd) {
            maxOf(map(selectionStart, false), floor).let { it to it }
        } else {
            floor to map(end, true)
        }
    }
}

/** Where a link goes in the text: an existing one around the selection ([existing]), or the selection itself. */
data class LinkTarget(
    val start: Int,
    val end: Int,
    val text: String,
    val address: String,
    val existing: Boolean,
    /** An existing link's title with its quotes (`"Karten"`), which the dialog doesn't show but keeps. */
    val title: String = "",
    /** A bare address, which the site makes a link of by itself. */
    val bare: Boolean = false,
)

/**
 * The link the selection or the cursor is in, or else the selection, for the link dialog; `null` in a code block or a
 * reference definition, where a link would be text. A reference link (`[Karten][karten]`) counts with its
 * definition's address and becomes an inline link when it is changed.
 */
fun linkAt(text: String, selectionStart: Int, selectionEnd: Int): LinkTarget? {
    val tree = parseMarkdown(text)
    val links = links(text, tree)
    // a cursor right before or after a link is outside it; a selection may take the whole link
    links.firstOrNull {
        if (selectionStart == selectionEnd) {
            selectionStart > it.start && selectionStart < it.end
        } else {
            selectionStart >= it.start && selectionEnd <= it.end
        }
    }?.let { return it }
    // spaces at the selection's ends stay outside the link, as the dialog trims its text; a link's text is one line
    var start = selectionStart
    var end = text.indexOf('\n', selectionStart).takeIf { it in 0 until selectionEnd } ?: selectionEnd
    while (start < end && text[start].isWhitespace()) start++
    while (end > start && text[end - 1].isWhitespace()) end--
    val nodes = tree.all().toList()
    if (nodes.any { !it.leaf && it.type in SYNTAX_BLOCKS && it.start <= end && start <= it.end }) {
        return null
    }
    // a heading's `#` and underline stay outside: the link goes into its text, and an empty `#` takes none
    nodes.firstOrNull { !it.leaf && it.type in HEADINGS && it.start <= start && start <= it.end }?.let { heading ->
        val (from, to) = headingText(text, heading) ?: return null
        start = start.coerceIn(from, to)
        end = end.coerceIn(start, to)
    }
    // links can't nest: links the selection touches go into the new one with their text, images and code whole
    val inside = links.filter { it.start < end && it.end > start }
    val whole = nodes.filter { (it.type == "IMAGE" || it.type == "CODE_SPAN") && !it.leaf && it.start < end && it.end > start }
    (inside.map { it.start to it.end } + whole.map { it.start to it.end }).forEach { (from, to) ->
        start = minOf(start, from)
        end = maxOf(end, to)
    }
    var label = text.substring(start, end)
    inside.sortedByDescending { it.start }.forEach { label = label.replaceRange(it.start - start, it.end - start, it.text) }
    return LinkTarget(start, end, label, "", existing = false)
}

/** The links in [text] as the site reads them: inline ones, and reference ones with their definition's address. */
private fun links(text: String, tree: MarkdownNode): List<LinkTarget> {
    fun MarkdownNode.text() = text.substring(start, end)
    // without the brackets of `[…]`
    fun MarkdownNode.inner() = text.substring(start + 1, end - 1)
    fun MarkdownNode.destination() = text().let { if (it.startsWith("<")) it.substring(1, it.length - 1) else it }

    // the first definition of a label counts, as on the site
    val definitions = mutableMapOf<String, Pair<String, String>>()
    tree.all().filter { it.type == "LINK_DEFINITION" }.forEach { definition ->
        definitions.getOrPut(normalizeLabel(definition.part("LINK_LABEL")!!.inner())) {
            Pair(definition.part("LINK_DESTINATION")?.destination() ?: "", definition.part("LINK_TITLE")?.text() ?: "")
        }
    }
    // an image's link is the image's, not a link
    return tree.all(skip = { it.type == "IMAGE" }).mapNotNull { link ->
        val linkText = link.part("LINK_TEXT")
        val label = link.part("LINK_LABEL")
        when (link.type) {
            "INLINE_LINK" -> LinkTarget(
                link.start,
                link.end,
                linkText!!.inner(),
                link.part("LINK_DESTINATION")?.destination() ?: "",
                existing = true,
                title = link.part("LINK_TITLE")?.text() ?: "",
            )
            "FULL_REFERENCE_LINK", "SHORT_REFERENCE_LINK" -> {
                val shown = linkText ?: label!!
                val key = label?.inner()?.takeIf { it.isNotBlank() } ?: shown.inner()
                definitions[normalizeLabel(key)]?.let { (address, title) ->
                    LinkTarget(link.start, link.end, shown.inner(), address, existing = true, title = title)
                }
            }
            // a bare address, which the site shows as a link to itself
            "GFM_AUTOLINK" -> link.text().let { address ->
                val href = when {
                    "://" in address || address.startsWith("mailto:", ignoreCase = true) -> address
                    '@' in address -> "mailto:$address"
                    else -> "http://$address"
                }
                LinkTarget(link.start, link.end, address, href, existing = true, bare = true)
            }
            else -> null
        }
    }.toList()
}

// as the parser's LinkMap does: no trimming, so `[ t ]` isn't `[t]`
private fun normalizeLabel(label: String) = label.lowercase().replace(Regex("\\s+"), " ")

/**
 * `[linkText](address)` in place of [target], with an existing link's title kept and the link's text selected.
 * Brackets in the text that would end it early are escaped; spaces in the address, which would end it, are written
 * as `%20`, and so are parentheses that don't pair up, as `%28` and `%29`.
 */
fun insertLink(target: LinkTarget, linkText: String, address: String): MarkdownEdit {
    val text = closeEscape(linkText)
    val label = if (closingBracket("$text]", 0) == text.length) text else escapeBrackets(text)
    val title = if (target.title.isEmpty()) "" else " ${target.title}"
    val destination = closeEscape(address.replace(WHITESPACE, "%20"))
        .let { if (balancedParentheses(it)) it else it.replace("(", "%28").replace(")", "%29") }
    return MarkdownEdit(target.start, target.end, "[$label]($destination$title)", target.start + 1, target.start + 1 + label.length)
}

/** The link's text in place of the link, selected. */
fun removeLink(target: LinkTarget): MarkdownEdit {
    // a bare address stays text with an escape that keeps the site from reading it as an address
    val text = if (!target.bare) {
        target.text
    } else {
        listOf("://", "www.", "@").firstOrNull { it in target.text }
            ?.let { target.text.replaceFirst(it, it.dropLast(1) + "\\" + it.last()) }
            ?: target.text
    }
    return MarkdownEdit(target.start, target.end, text, target.start, target.start + text.length)
}

/** The link dialog's errors for the trimmed [text] and [address], `null` where it is fine. */
fun linkErrors(text: String, address: String): Pair<String?, String?> = Pair(
    "Gib den Text des Links ein.".takeIf { text.isEmpty() },
    when {
        address.isEmpty() -> "Gib die Adresse ein."
        !address.startsWith("https://", ignoreCase = true) && !address.startsWith("mailto:", ignoreCase = true) ->
            "Die Adresse muss mit https:// oder mailto: beginnen."
        else -> null
    },
)

/** The parts whose text emphasis can mark, each on its own: paragraphs, headings' text and table cells. */
private val INLINE_BLOCKS = setOf("PARAGRAPH", "ATX_CONTENT", "SETEXT_CONTENT", "CELL")
private val SYNTAX_BLOCKS = setOf("CODE_FENCE", "CODE_BLOCK", "LINK_DEFINITION")
private val LINKS = setOf("INLINE_LINK", "FULL_REFERENCE_LINK", "SHORT_REFERENCE_LINK")
private val HEADINGS = setOf("ATX_1", "ATX_2", "ATX_3", "ATX_4", "ATX_5", "ATX_6", "SETEXT_1", "SETEXT_2")

// the start of a line's text that would read as a list marker: `2` of `2. ` or `-` of `- `
private val ESCAPED_LIST_LIKE = Regex("""^(?:\d{1,9}(?=\\[.)](?:[ \t]|$))|(?=\\[-*+](?:[ \t]|$)))""")
private val LIST_LIKE = Regex("""^(?:\d{1,9}(?=[.)](?:[ \t]|$))|[-*+](?=[ \t]|$))""")
private val WHITESPACE = Regex("""\s""")

/** A list item: its marker (with the space after it), whether it is a bullet, and the item it is nested in. */
private class ListItem(val marker: Marker, val bullet: Boolean, val parent: ListItem?, val depth: Int, val start: Int, val end: Int)

/** Where a list marker is; the parser's token also holds the indent before it, which this leaves out. */
private class Marker(val start: Int, val end: Int)

/** The list items below this node, in [text]'s order. */
private fun MarkdownNode.items(text: String, parent: ListItem? = null, depth: Int = 0): List<ListItem> = children.flatMap { child ->
    if (!child.leaf && child.type == "LIST_ITEM") {
        val token = child.children.first { it.type == "LIST_BULLET" || it.type == "LIST_NUMBER" }
        val indent = text.substring(token.start, token.end).takeWhile { it == ' ' || it == '\t' }.length
        val item = ListItem(Marker(token.start + indent, token.end), token.type == "LIST_BULLET", parent, depth, child.start, child.end)
        listOf(item) + child.items(text, item, depth + 1)
    } else {
        child.items(text, parent, depth)
    }
}

private fun MarkdownNode.part(type: String) = children.firstOrNull { it.type == type }

/** This node and all below it, without what is below a node [skip] holds. */
private fun MarkdownNode.all(skip: (MarkdownNode) -> Boolean = { false }): Sequence<MarkdownNode> = sequence {
    yield(this@all)
    if (!skip(this@all)) {
        children.forEach { yieldAll(it.all(skip)) }
    }
}

/** Where the text of an emphasis starts and ends, inside its markers. */
private val MarkdownNode.contentStart get() = start + if (type == "STRONG") 2 else 1
private val MarkdownNode.contentEnd get() = end - if (type == "STRONG") 2 else 1

/** Where a heading's text is, without the spaces around it; `null` for a heading without any (`#`). */
private fun headingText(text: String, heading: MarkdownNode): Pair<Int, Int>? =
    heading.children.firstOrNull { it.type == "ATX_CONTENT" || it.type == "SETEXT_CONTENT" }
        ?.let { trimmed(text, it.start, it.end) ?: (it.end to it.end) }

/** The column of [at] in the line starting at [lineStart], with tabs to the next multiple of 4 as Markdown reads them. */
private fun column(text: String, lineStart: Int, at: Int): Int =
    (lineStart until at).fold(0) { column, i -> if (text[i] == '\t') column + 4 - column % 4 else column + 1 }

/** [from]..[to] without spaces at its ends, or `null` if nothing is left. */
private fun trimmed(text: String, from: Int, to: Int): Pair<Int, Int>? {
    var start = from
    var end = to
    while (start < end && text[start].isWhitespace()) start++
    while (end > start && text[end - 1].isWhitespace()) end--
    return (start to end).takeIf { start < end }
}

/** The lines from [start] to [end], each from its first character to its line break (or the end). */
private fun lineRanges(text: String, start: Int, end: Int): List<IntRange> {
    val lines = mutableListOf<IntRange>()
    var at = start
    while (true) {
        val lineEnd = text.indexOf('\n', at).takeIf { it in 0 until end } ?: end
        lines += at..lineEnd
        if (lineEnd >= end) return lines
        at = lineEnd + 1
    }
}

/** A replacement of [start]..[end] with [text], one of the changes that make up an edit. */
private class Change(val start: Int, val end: Int, val text: String)

/**
 * The [changes] as one edit over at least [start]..[end], selecting what [select] gives with a function that maps a
 * place in the old text to the new one: a place inside a replaced part goes to its end, a place where something is
 * inserted stays before it unless `after`.
 */
private fun combine(text: String, start: Int, end: Int, changes: List<Change>, select: ((Int, Boolean) -> Int) -> Pair<Int, Int>): MarkdownEdit {
    val sorted = changes.sortedWith(compareBy({ it.start }, { it.end }))
    val from = minOf(start, sorted.minOfOrNull { it.start } ?: start)
    val to = maxOf(end, sorted.maxOfOrNull { it.end } ?: end)
    val replacement = StringBuilder()
    var at = from
    sorted.forEach { change ->
        replacement.append(text, at, change.start).append(change.text)
        at = change.end
    }
    replacement.append(text, at, to)
    val map = { position: Int, after: Boolean ->
        var shift = 0
        var mapped: Int? = null
        for (change in sorted) {
            if (change.end < position || (change.end == position && (change.start < position || after))) {
                shift += change.text.length - (change.end - change.start)
            } else {
                if (change.start < position) mapped = change.start + shift + change.text.length
                break
            }
        }
        mapped ?: (position + shift)
    }
    val (selectionStart, selectionEnd) = select(map)
    return MarkdownEdit(from, to, replacement.toString(), selectionStart, selectionEnd)
}

/**
 * The `]` that ends a link's text starting at [from]: brackets inside it are balanced, escaped ones are skipped;
 * -1 at a line break.
 */
private fun closingBracket(text: String, from: Int): Int {
    var depth = 0
    var index = from
    while (index < text.length) {
        when (text[index]) {
            '\\' -> index++
            '[' -> depth++
            ']' -> if (depth == 0) return index else depth--
            '\n' -> return -1
        }
        index++
    }
    return -1
}

/** [text] with a backslash before each bracket that isn't escaped yet, which an even run of backslashes doesn't do. */
private fun escapeBrackets(text: String): String {
    val escaped = StringBuilder()
    var backslashes = 0
    text.forEach { char ->
        if ((char == '[' || char == ']') && backslashes % 2 == 0) escaped.append('\\')
        escaped.append(char)
        backslashes = if (char == '\\') backslashes + 1 else 0
    }
    return escaped.toString()
}

/** [value] with a backslash at its end doubled, so it doesn't escape the `]` or `)` after it. */
private fun closeEscape(value: String) = if (value.takeLastWhile { it == '\\' }.length % 2 == 1) value + "\\" else value

private fun balancedParentheses(address: String): Boolean {
    var depth = 0
    var index = 0
    while (index < address.length) {
        when (address[index]) {
            '\\' -> index++
            '(' -> depth++
            ')' -> if (--depth < 0) return false
        }
        index++
    }
    return depth == 0
}

private fun Char.isWordChar() = isLetterOrDigit() || this == '_' || this == '-'
